/**
 * API Client for Majalisna Application
 * 
 * This service provides configured axios instances for making API calls,
 * with separate clients for public and authenticated routes.
 * 
 * This is the base module that all service modules will use.
 */

import axios from 'axios';
import API from '../config/api';
import logger from '../utils/logger';

// Create logger instance for API client
const apiLogger = logger.create('ApiClient');

// Create a public axios instance for requests that don't need an auth token
export const publicApiClient = axios.create({
  baseURL: API.BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// Create a private axios instance for requests that require an auth token
export const privateApiClient = axios.create({
  baseURL: API.BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor ONLY to the private instance
privateApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle common errors (e.g., redirect on 401)
privateApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token is invalid or expired
      apiLogger.warn('Authentication token expired or invalid');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Helper function to handle API errors consistently
 * @param {Error} error - The error object from axios
 * @returns {Object} Standardized error object
 */
export const handleApiError = (error) => {
  let errorMessage = 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.';
  let statusCode = 500;
  
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    statusCode = error.response.status;
    errorMessage = error.response.data?.message || errorMessage;
    
    if (statusCode === 401) {
      errorMessage = 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.';
    } else if (statusCode === 403) {
      errorMessage = 'ليس لديك صلاحية للوصول إلى هذا المورد.';
    } else if (statusCode === 404) {
      errorMessage = 'المورد المطلوب غير موجود.';
    }
  } else if (error.request) {
    // The request was made but no response was received
    errorMessage = 'لا يمكن الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.';
  } else {
    // Something happened in setting up the request that triggered an Error
    errorMessage = error.message || errorMessage;
  }
  
  apiLogger.error(`API Error: ${statusCode} - ${errorMessage}`, error);
  
  return {
    message: errorMessage,
    statusCode,
    originalError: error
  };
};

// تعريف كائن apiClient لتصديره
const apiClient = {
  publicApiClient,
  privateApiClient,
  handleApiError
};

// تصدير الكائن
export default apiClient;